import type { Database } from "../../../../../packages/types/src/database/index";
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from "./service-registry";
import { CreateTodoRequest, Todo, UpdateTodoRequest } from "@/types/todo";
import { logger } from "@/lib/logger";
import { taskActivityService } from "@flowstate/core";
import { notificationManager } from "@flowstate/core";
import { NOTIFICATION_TYPES } from "@/types/notification";
import { typedSupabase } from "@/lib/typed-supabase";
import { processMentions } from "@/utils/mentionUtils";
import { applyTodoFilters, normalizeTodoStatus } from "@/utils/taskFilters";
import type { TodoQueryFilters } from "./todo/todo-core.service";

// Import sub-services
import { todoCoreService, ITodoCoreService } from "./todo/todo-core.service";
import { todoAssignmentService, ITodoAssignmentService } from "./todo/todo-assignment.service";
import { todoRecurrenceService, ITodoRecurrenceService } from "./todo/todo-recurrence.service";
import { todoDependencyService, ITodoDependencyService } from "./todo/todo-dependency.service";
import { todoSyncService, ITodoSyncService } from "./todo/todo-sync.service";

export interface ITodoService extends IService {
  /** @deprecated Caching is now handled by the UI layer (React Query) */
  getTodos(_useCache?: boolean, organizationId?: string): Promise<ServiceResponse<Todo[]>>;
  /** @deprecated Caching is now handled by the UI layer (React Query) */
  getFilteredTodos(options?: FilterOptions, organizationId?: string): Promise<ServiceResponse<Todo[]>>;
  createTodo(todo: CreateTodoRequest): Promise<ServiceResponse<Todo>>;
  updateTodo(
    id: string,
    updates: UpdateTodoRequest
  ): Promise<ServiceResponse<Todo>>;
  deleteTodo(id: string): Promise<ServiceResponse<void>>;
  getSubtasks(parentId: string, organizationId?: string): Promise<ServiceResponse<Todo[]>>;
  createSubtask(
    parentId: string,
    subtask: Omit<CreateTodoRequest, "parent_id">
  ): Promise<ServiceResponse<Todo>>;
  hasIncompleteSubtasks(taskId: string): Promise<ServiceResponse<boolean>>;
  getTaskWithSubtasks(
    taskId: string
  ): Promise<ServiceResponse<{ task: Todo; subtasks: Todo[] }>>;
  getTopLevelTodos(useDatabase?: boolean): Promise<ServiceResponse<Todo[]>>;
  syncWithGoogleSheets(): Promise<ServiceResponse<void>>;
  validateTaskHierarchy(
    taskId: string,
    parentId: string
  ): Promise<ServiceResponse<boolean>>;
  getAllDescendants(taskId: string): Promise<ServiceResponse<Todo[]>>;
  clearTodosCache(): ServiceResponse<void>;
  invalidateCache(
    operation: "todos" | "subtasks" | "specific",
    /** @deprecated ID parameter is currently not used in the manual invalidation trigger */
    _id?: string
  ): ServiceResponse<void>;
  getTodoStats(userId?: string): Promise<ServiceResponse<TodoStats>>;
  searchTodos(searchTerm: string, filters?: {
    status?: string;
    priority?: string;
    assignedToUserId?: string;
  }): Promise<ServiceResponse<Todo[]>>;
  getCacheStats(): ServiceResponse<CacheStats>;
  archiveTaskAttachments(
    taskId: string
  ): Promise<ServiceResponse<ArchiveResult>>;
  getArchivedAttachments(
    taskId: string
  ): Promise<
    ServiceResponse<
      Database["public"]["Functions"]["get_archived_attachments"]["Returns"]
    >
  >;
  archiveCompletedTodosWithAttachments(
    daysOld?: number
  ): Promise<ServiceResponse<ArchiveCompletedResult>>;
  getProfileTasks(
    userId: string,
    /** @deprecated Caching is now handled by the UI layer (React Query) */
    _useCache?: boolean
  ): Promise<ServiceResponse<Todo[]>>;
  getProfileSubtasks(
    parentId: string,
    /** @deprecated Caching is now handled by the UI layer (React Query) */
    _useCache?: boolean
  ): Promise<ServiceResponse<Todo[]>>;
  /** @deprecated Profile caching is now handled by the UI layer (React Query) */
  invalidateProfileCache(_userId?: string): ServiceResponse<void>;
  getProfileCacheStats(): ServiceResponse<ProfileCacheStats>;
  clearProfileCache(): ServiceResponse<void>;
}

export interface TodoStats {
  total: number;
  completed: number;
  active: number;
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
  highPriority: number;
  assignedToMe: number;
}

export interface FilterOptions {
  parentOnly?: boolean;
  searchTerm?: string;
  status?: string;
  priority?: string;
  /** @deprecated Caching is now handled by the UI layer (React Query) */
  useCache?: boolean;
  includeCompleted?: boolean;
  assignmentFilter?: "all" | "created_by_me" | "assigned_to_me";
  createdByUserId?: string;
  assignedToUserId?: string;
  categoryId?: string;
  clientId?: string;
  projectId?: string;
  /** @deprecated Field captured but not yet implemented in the query engine */
  scheduled?: boolean;
  dateRange?: {
    start: string;
    end: string;
  };
  // Pagination support
  limit?: number;
  offset?: number;
  // Sorting options
  sortBy?: 'created_at' | 'updated_at' | 'title' | 'priority' | 'due_date';
  sortOrder?: 'asc' | 'desc';
}

export interface CacheStats {
  allTodos: { size: number; age: number; isValid: boolean };
  parentTodos: { size: number; age: number; isValid: boolean };
  performance?: { slowOperations: [string, number][]; cacheStats: unknown };
}

export interface ArchiveResult {
  success: boolean;
  archivedCount: number;
  error?: string;
}

export interface ArchiveCompletedResult {
  todosArchived: number;
  attachmentsArchived: number;
  errors: string[];
}

export interface ProfileCacheStats {
  tasks: { count: number; size: string; oldestEntry: Date | undefined };
  subtasks: { count: number; size: string; oldestEntry: Date | undefined };
  memoryUsage: { estimated: string; efficiency: number };
}

export class TodoService implements ITodoService {
  readonly name = "todo" as const;

  // Sub-service dependencies
  private coreService: ITodoCoreService;
  private assignmentService: ITodoAssignmentService;
  private recurrenceService: ITodoRecurrenceService;
  private dependencyService: ITodoDependencyService;
  private syncService: ITodoSyncService;

  constructor(
    coreService: ITodoCoreService = todoCoreService,
    assignmentService: ITodoAssignmentService = todoAssignmentService,
    recurrenceService: ITodoRecurrenceService = todoRecurrenceService,
    dependencyService: ITodoDependencyService = todoDependencyService,
    syncService: ITodoSyncService = todoSyncService
  ) {
    this.coreService = coreService;
    this.assignmentService = assignmentService;
    this.recurrenceService = recurrenceService;
    this.dependencyService = dependencyService;
    this.syncService = syncService;
  }

  async initialize(): Promise<void> {
    logger.info("🚀 Todo Service (Orchestrator) initialized");

    // Initialize all sub-services
    await Promise.all([
      this.coreService.initialize!(),
      this.assignmentService.initialize!(),
      this.recurrenceService.initialize!(),
      this.dependencyService.initialize!(),
      this.syncService.initialize!(),
    ]);

    logger.info("📊 All sub-services initialized successfully");
  }

  async dispose(): Promise<void> {
    logger.info("🛑 Todo Service (Orchestrator) disposing");

    // Dispose all sub-services
    await Promise.all([
      this.coreService.dispose!(),
      this.assignmentService.dispose!(),
      this.recurrenceService.dispose!(),
      this.dependencyService.dispose!(),
      this.syncService.dispose!(),
    ]);

    logger.info("✅ All sub-services disposed successfully");
  }

  // ====================================================================================================
  // 🎯 SUBTASK OPERATIONS - Delegated to core service
  // ====================================================================================================

  async getSubtasks(parentId: string, organizationId?: string): Promise<ServiceResponse<Todo[]>> {
    return this.coreService.getSubtasksByParentId(parentId, organizationId);
  }

  async createSubtask(parentId: string, subtask: Omit<CreateTodoRequest, "parent_id">): Promise<ServiceResponse<Todo>> {
    const parentResult = await this.coreService.getTodo(parentId);
    if (!parentResult.success || !parentResult.data) {
      return createErrorResponse('Parent task not found or inaccessible');
    }
    const parent = parentResult.data;
    const organizationId = parent.organization_id ?? subtask.organization_id;
    
    if (!organizationId) {
      return createErrorResponse('Cannot create subtask: parent task has no organization_id and none provided');
    }
    
    const subtaskWithParent = {
      ...subtask,
      parent_id: parentId,
      organization_id: organizationId,
    };
    return this.coreService.createTodo(subtaskWithParent);
  }

  async hasIncompleteSubtasks(_taskId: string): Promise<ServiceResponse<boolean>> {
    // This requires cross-service logic - check dependencies and subtasks
    // For now, return a placeholder implementation
    return createSuccessResponse(false);
  }

  async getTaskWithSubtasks(_taskId: string): Promise<ServiceResponse<{ task: Todo; subtasks: Todo[] }>> {
    // This requires cross-service logic
    // For now, return a placeholder implementation
    return createErrorResponse('Not implemented in orchestrator yet');
  }

  async getTopLevelTodos(_useDatabase?: boolean): Promise<ServiceResponse<Todo[]>> {
    return this.coreService.getTopLevelTodos();
  }

  // Private helper methods removed - kept in git history if needed in future
  // Former transformToTodo and validateParentId methods were unused

  private withRetry = async <T>(
    operation: () => Promise<T>,
    retries = 2,
    delay = 1000
  ): Promise<T> => {
    try {
      return await operation();
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        logger.warn(
          `Operation failed, retrying in ${delay}ms... (${retries} retries left)`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.withRetry(operation, retries - 1, delay * 2);
      }
      throw error;
    }
  };

  private shouldRetry = (error: unknown): boolean => {
    if (error && typeof error === "object" && "code" in error) {
      const code = (error as { code: string }).code;
      return ["NETWORK_ERROR", "TIMEOUT", "503", "502", "504"].includes(code);
    }
    return false;
  };

  // ====================================================================================================
  // 🎯 CORE CRUD OPERATIONS - Delegated to sub-services
  // ====================================================================================================

  /** @deprecated useCache is ignored; caching is handled by React Query in the UI */
  async getTodos(_useCache: boolean = false, organizationId?: string): Promise<ServiceResponse<Todo[]>> {
    return this.coreService.getTodos(organizationId);
  }

  /** @deprecated useCache is ignored; caching is handled by React Query in the UI */
  async getFilteredTodos(options?: FilterOptions, organizationId?: string): Promise<ServiceResponse<Todo[]>> {
    let createdByUserId = options?.createdByUserId;
    let assignedToUserId = options?.assignedToUserId;

    if (
      !createdByUserId &&
      !assignedToUserId &&
      options?.assignmentFilter &&
      options.assignmentFilter !== "all"
    ) {
      const { data: { user } } = await typedSupabase.auth.getUser();
      if (user?.id) {
        if (options.assignmentFilter === "created_by_me") {
          createdByUserId = user.id;
        } else if (options.assignmentFilter === "assigned_to_me") {
          assignedToUserId = user.id;
        }
      }
    }

    const status = normalizeTodoStatus(options?.status);
    const queryFilters: TodoQueryFilters = {
      parentOnly: options?.parentOnly,
      includeCompleted: options?.includeCompleted ?? false,
      searchTerm: options?.searchTerm,
      status,
      priority: options?.priority && options.priority !== "all" ? options.priority : undefined,
      createdByUserId,
      assignedToUserId,
      categoryId: options?.categoryId,
      clientId: options?.clientId,
      projectId: options?.projectId,
      dueAfter: options?.dateRange?.start,
      dueBefore: options?.dateRange?.end,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    };

    const result = await this.coreService.getTodos(organizationId, queryFilters);
    if (!result.success || !result.data) return result;

    let todos = applyTodoFilters(result.data, {
      ...options,
      status,
      createdByUserId,
      assignedToUserId,
      currentUserId: createdByUserId ?? assignedToUserId,
      includeCompleted: options?.includeCompleted ?? false,
    });

    if (options?.offset != null || options?.limit != null) {
      const start = options.offset ?? 0;
      const end = options.limit != null ? start + options.limit : undefined;
      todos = todos.slice(start, end);
    }

    return createSuccessResponse(todos);
  }

  async createTodo(todo: CreateTodoRequest): Promise<ServiceResponse<Todo>> {
    // First, delegate to core service for creation
    logger.info('🚀 TodoService.createTodo: processing request', { todo });
    const result = await this.coreService.createTodo(todo);
    
    if (!result.success || !result.data) {
      return result;
    }
    
    const createdTodo = result.data;
    
    // Process @mentions in description and send notifications
    if (createdTodo.description) {
      try {
        const mentionResult = await processMentions(createdTodo.description);
        for (const userId of mentionResult.mentions) {
          await notificationManager.createNotification({
            type: NOTIFICATION_TYPES.TASK_MENTION,
            user_id: userId,
            title: 'You were mentioned in a task',
            message_preview: `${createdTodo.title}: ${createdTodo.description?.substring(0, 100)}...`,
            data: {
              todo_id: createdTodo.id,
              mentioner: createdTodo.created_by,
            },
          });
        }
      } catch (mentionError) {
        logger.warn('Failed to process mentions', { error: mentionError, todoId: createdTodo.id });
      }
    }
    
    // Log activity for todo creation
    try {
      await taskActivityService.logTaskActivity({
        taskId: createdTodo.id,
        actionType: 'created',
        details: { title: createdTodo.title },
      });
    } catch (activityError) {
      logger.warn('Failed to log creation activity', { error: activityError, todoId: createdTodo.id });
    }
    
    // Handle recurrence if specified
    if ('recurrence' in todo && todo.recurrence) {
      try {
        await this.recurrenceService.createRecurringTodo(todo, todo.recurrence as unknown as Parameters<typeof this.recurrenceService.createRecurringTodo>[1]);
      } catch (recurrenceError) {
        logger.warn('Failed to create recurring instances', { error: recurrenceError, todoId: createdTodo.id });
      }
    }
    
    return result;
  }

  async updateTodo(id: string, updates: UpdateTodoRequest): Promise<ServiceResponse<Todo>> {
    // Get current todo for comparison
    const currentResult = await this.coreService.getTodo(id);
    if (!currentResult.success || !currentResult.data) {
      return createErrorResponse(currentResult.error ?? 'Todo not found');
    }
    
    const currentTodo = currentResult.data;
    
    // Delegate to core service for update
    const result = await this.coreService.updateTodo(id, updates);
    
    if (!result.success || !result.data) {
      return result;
    }
    
    const updatedTodo = result.data;
    
    // Check if assignment changed and notify
    if (updates.assigned_to !== undefined && updates.assigned_to !== currentTodo.assigned_to) {
      try {
        if (updates.assigned_to) {
          // Notify the new assignee
          await notificationManager.createNotification({
            type: NOTIFICATION_TYPES.TASK_ASSIGNED,
            user_id: updates.assigned_to,
            title: 'Task Assigned',
            message_preview: `You have been assigned: ${updatedTodo.title}`,
            data: {
              todo_id: id,
              assigned_by: updates.assigned_by ?? currentTodo.created_by,
            },
          });
        }
      } catch (notificationError) {
        logger.warn('Failed to send assignment notification', { error: notificationError, todoId: id });
      }
    }
    
    // Log activity for todo update
    try {
      await taskActivityService.logTaskActivity({
        taskId: id,
        actionType: 'updated',
        details: {
          changes: Object.keys(updates),
          previous_status: currentTodo.status,
          new_status: updatedTodo.status,
        },
      });
    } catch (activityError) {
      logger.warn('Failed to log update activity', { error: activityError, todoId: id });
    }
    
    // Process mentions in updated description
    if (updatedTodo.description && updates.description !== currentTodo.description) {
      try {
        const mentionResult = await processMentions(updatedTodo.description);
        for (const userId of mentionResult.mentions) {
          await notificationManager.createNotification({
            type: NOTIFICATION_TYPES.TASK_MENTION,
            user_id: userId,
            title: 'You were mentioned in a task',
            message_preview: `${updatedTodo.title}: ${updatedTodo.description?.substring(0, 100)}...`,
            data: {
              todo_id: id,
            },
          });
        }
      } catch (mentionError) {
        logger.warn('Failed to process mentions', { error: mentionError, todoId: id });
      }
    }
    
    return result;
  }

  async deleteTodo(id: string): Promise<ServiceResponse<void>> {
    // Get current todo for activity logging before deletion
    const currentResult = await this.coreService.getTodo(id);
    const currentTodo = currentResult.success ? currentResult.data : null;
    
    // Delegate to core service for deletion
    const result = await this.coreService.deleteTodo(id);
    
    if (!result.success) {
      return result;
    }
    
    // Log activity for todo deletion
    if (currentTodo) {
      try {
        await taskActivityService.logTaskActivity({
          taskId: id,
          actionType: 'deleted',
          details: { title: currentTodo.title },
        });
      } catch (activityError) {
        logger.warn('Failed to log deletion activity', { error: activityError, todoId: id });
      }
    }
    
    return result;
  }

  // ====================================================================================================
  // 🎯 ASSIGNMENT OPERATIONS - Delegated to assignment service
  // ====================================================================================================

  async getProfileTasks(userId: string, _useCache?: boolean): Promise<ServiceResponse<Todo[]>> {
    return this.assignmentService.getProfileTasks(userId);
  }

  async getProfileSubtasks(parentId: string, _useCache?: boolean): Promise<ServiceResponse<Todo[]>> {
    return this.assignmentService.getProfileSubtasks(parentId);
  }

  // ====================================================================================================
  // 🎯 SYNC & INTEGRATION OPERATIONS - Delegated to sync service
  // ====================================================================================================

  async syncWithGoogleSheets(): Promise<ServiceResponse<void>> {
    const result = await this.syncService.syncWithGoogleSheets();
    // Cast to void response - the sync result contains details we don't need to expose
    if (!result.success) {
      return { success: false, error: result.error };
    }

    return { success: true, data: undefined };
  }

  // ====================================================================================================
  // 🎯 HIERARCHY & DEPENDENCY OPERATIONS - Delegated to dependency service
  // ====================================================================================================

  async validateTaskHierarchy(taskId: string, parentId: string): Promise<ServiceResponse<boolean>> {
    return this.dependencyService.validateDependencyChain(taskId, parentId);
  }

  async getAllDescendants(_taskId: string): Promise<ServiceResponse<Todo[]>> {
    // This requires getting all descendants recursively
    // For now, return placeholder - could be implemented in dependency service
    return createErrorResponse('Not implemented in orchestrator yet');
  }

  // ====================================================================================================
  // 🎯 STATISTICS & ANALYTICS - Delegated to core service (for now)
  // ====================================================================================================

  async getTodoStats(userId?: string): Promise<ServiceResponse<TodoStats>> {
    try {
      let query = typedSupabase.from('todos').select('status, priority, due_date, assigned_to');
      
      if (userId) {
        query = query.or(`assigned_to.eq.${userId},created_by.eq.${userId}`);
      }

      const { data: todos, error } = await query;
      
      if (error) {
        return createErrorResponse('Failed to fetch todos for stats calculation');
      }

      if (!todos) {
        return createSuccessResponse({
          total: 0, completed: 0, active: 0, overdue: 0, 
          dueToday: 0, dueThisWeek: 0, highPriority: 0, assignedToMe: 0
        });
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const stats: TodoStats = {
        total: todos.length,
        completed: todos.filter(t => t.status === "completed").length,
        active: todos.filter(t => t.status !== "completed").length,
        overdue: todos.filter(t =>
          t.status !== "completed" &&
          t.due_date &&
          new Date(t.due_date) < today
        ).length,
        dueToday: todos.filter(t => {
          if (!t.due_date || t.status === "completed") {
            return false;
          }
          const dueDate = new Date(t.due_date);
          return dueDate >= today && dueDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
        }).length,
        dueThisWeek: todos.filter(t => {
          if (!t.due_date || t.status === "completed") {
            return false;
          }
          const dueDate = new Date(t.due_date);
          const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          return dueDate >= today && dueDate <= weekEnd;
        }).length,
        highPriority: todos.filter(t => t.priority === "high" && t.status !== "completed").length,
        assignedToMe: userId ? todos.filter(t => t.assigned_to === userId).length : 0,
      };

      return createSuccessResponse(stats);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Failed to calculate stats');
    }
  }

  // ====================================================================================================
  // 🎯 SEARCH OPERATIONS - Delegated to core service (for now)
  // ====================================================================================================

  async searchTodos(searchTerm: string, filters?: {
    status?: string;
    priority?: string;
    assignedToUserId?: string;
  }): Promise<ServiceResponse<Todo[]>> {
    try {
      const result = await this.coreService.getTodos(undefined, {
        searchTerm,
        status: filters?.status,
        priority: filters?.priority,
        assignedToUserId: filters?.assignedToUserId,
        limit: 50,
      });

      if (!result.success) {
        return result;
      }

      return createSuccessResponse(result.data || []);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Search failed');
    }
  }

  // ====================================================================================================
  // 🎯 CACHE MANAGEMENT - Handled by React Query (deprecated)
  // ====================================================================================================

  clearTodosCache(): ServiceResponse<void> {
    return createSuccessResponse(undefined);
  }

  invalidateCache(
    _operation: "todos" | "subtasks" | "specific",
    _id?: string
  ): ServiceResponse<void> {
    return createSuccessResponse(undefined);
  }

  getCacheStats(): ServiceResponse<CacheStats> {
    return createSuccessResponse({
      allTodos: { size: 0, age: 0, isValid: true },
      parentTodos: { size: 0, age: 0, isValid: true },
      performance: undefined,
    });
  }

  // ====================================================================================================
  // 🎯 ARCHIVE OPERATIONS - Delegated to sync service (for now)
  // ====================================================================================================

  async archiveTaskAttachments(_taskId: string): Promise<ServiceResponse<ArchiveResult>> {
    // This involves file operations and could be in sync service
    // For now, return placeholder
    return createErrorResponse('Not implemented in orchestrator yet');
  }

  async getArchivedAttachments(_taskId: string): Promise<ServiceResponse<Database["public"]["Functions"]["get_archived_attachments"]["Returns"]>> {
    // This involves database function calls
    // For now, return placeholder
    return createErrorResponse('Not implemented in orchestrator yet');
  }

  async archiveCompletedTodosWithAttachments(daysOld?: number): Promise<ServiceResponse<ArchiveCompletedResult>> {
    const result = await this.syncService.syncWithExternalSystem('archive', { daysOld });
    if (!result.success || !result.data) {
      return createErrorResponse(result.error ?? 'Archive failed');
    }
    // Map the sync result to ArchiveCompletedResult
    return createSuccessResponse({
      todosArchived: result.data.syncedCount || 0,
      attachmentsArchived: result.data.syncedCount || 0,
      errors: [],
    });
  }

  // ====================================================================================================
  // 🎯 PROFILE CACHE MANAGEMENT - Handled by React Query (deprecated)
  // ====================================================================================================

  invalidateProfileCache(_userId?: string): ServiceResponse<void> {
    return createSuccessResponse(undefined);
  }

  getProfileCacheStats(): ServiceResponse<ProfileCacheStats> {
    return createSuccessResponse({
      tasks: { count: 0, size: "0 KB", oldestEntry: undefined },
      subtasks: { count: 0, size: "0 KB", oldestEntry: undefined },
      memoryUsage: { estimated: "0 MB", efficiency: 1.0 },
    });
  }

  clearProfileCache(): ServiceResponse<void> {
    return createSuccessResponse(undefined);
  }
}

// Export singleton instance
export const todoService = new TodoService();
